import { useEffect, useMemo, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, Loader2 } from "lucide-react";
import { toastApiError } from "@/lib/toastApiError";

interface AudioUploadFieldProps {
  /** Existing URL from server (playback). */
  remoteUrl?: string;
  /** Locally selected file, sent as multipart `audio` on save. */
  pendingFile?: File | null;
  onPendingFileChange: (file: File | null) => void;
  /** Busy state when parent submits the term. */
  busy?: boolean;
}

export function AudioUploadField({
  remoteUrl,
  pendingFile,
  onPendingFileChange,
  busy = false,
}: AudioUploadFieldProps) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);

  const localPreviewUrl = useMemo(
    () => (pendingFile ? URL.createObjectURL(pendingFile) : null),
    [pendingFile]
  );

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const playableSrc =
    pendingFile != null ? localPreviewUrl || "" : remoteUrl?.trim() || "";

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (
      file.type.startsWith("audio/") ||
      /\.(mp3|m4a|wav|ogg|aac|webm)$/i.test(file.name)
    ) {
      onPendingFileChange(file);
    } else {
      toastApiError(t("form.audioInvalidType"));
    }
    e.target.value = "";
  };

  const clearPending = () => {
    if (busy) {
      return;
    }
    onPendingFileChange(null);
  };

  return (
    <div className="grid gap-2">
      <Label>{t("form.audioLabel")}</Label>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.mp3,.m4a,.wav,.ogg,.aac,.webm"
        className="hidden"
        onChange={handlePick}
      />
      {pendingFile || playableSrc ? (
        <div className="bg-muted/30 flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center">
          <audio
            controls
            src={playableSrc}
            className="h-8 min-h-10 w-full min-w-0 flex-1"
          />
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              {pendingFile
                ? t("form.audioPickDifferent")
                : t("form.audioAttachReplace")}
            </Button>
            {pendingFile ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearPending}
                disabled={busy}
              >
                {t("form.audioRemoveNew")}
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="justify-start"
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
              {t("form.audioSaving")}
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" /> {t("form.audioAttach")}
            </>
          )}
        </Button>
      )}
    </div>
  );
}
